import { create } from 'zustand';
import { useConfig } from '@/api/queries';

/** A face seen this recently (gate or in-call) satisfies the pre-matching gate. */
export const VERIFIED_FOR_MS = 2 * 60_000;

interface PendingGate {
  title: string;
  resolve: (ok: boolean) => void;
}

interface FaceState {
  verifiedAt: number;
  markVerified: (at?: number) => void;
  pending: PendingGate | null;
  open: (p: PendingGate) => void;
  settle: (ok: boolean) => void;
}

export const useFaceStore = create<FaceState>((set, get) => ({
  verifiedAt: 0,
  markVerified: (at = Date.now()) => set({ verifiedAt: at }),
  pending: null,
  open: (pending) => {
    get().pending?.resolve(false);
    set({ pending });
  },
  settle: (ok) => {
    const p = get().pending;
    set({ pending: null });
    p?.resolve(ok);
  },
}));

export function recentlyVerified(now = Date.now()): boolean {
  return now - useFaceStore.getState().verifiedAt < VERIFIED_FOR_MS;
}

/** Dev-only escape hatch for automated tests (SPEC_V2 §7). Never active in production builds. */
export function faceCheckDevOff(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return window.localStorage.getItem('popin.faceCheck') === 'off';
  } catch {
    return false;
  }
}

export type FaceMode = 'loading' | 'required' | 'off';

/** `GET /v1/config.face_check` ('required' by default) + the dev escape hatch. */
export function useFaceMode(): FaceMode {
  const config = useConfig();
  if (faceCheckDevOff()) return 'off';
  if (config.isLoading) return 'loading';
  return config.data?.face_check === 'off' ? 'off' : 'required';
}
